import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IconChevronRight } from '@tabler/icons-react';
import { useAuth } from '@/contexts/AuthContext';
import { navigation, NavigationItem, NavigationChild, NavigationSubChild } from '@/menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import Profile from './Header/HeaderContent/Profile';

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { hasPageAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const [openSubMenus, setOpenSubMenus] = useState<Set<string>>(new Set());

  const accessibleNavigation = navigation
    .map((item) => {
      if (item.children && item.children.length > 0) {
        const accessibleChildren = item.children
          .map((child) => {
            if (child.children && child.children.length > 0) {
              const accessibleSubChildren = child.children.filter((sub) =>
                hasPageAccess(sub.pageKey)
              );
              if (accessibleSubChildren.length === 0) return null;
              return { ...child, children: accessibleSubChildren };
            }
            return hasPageAccess(child.pageKey) ? child : null;
          })
          .filter((child): child is NavigationChild => child !== null);
        if (accessibleChildren.length === 0) {
          return hasPageAccess(item.pageKey)
            ? { ...item, children: undefined }
            : null;
        }
        return { ...item, children: accessibleChildren };
      }
      return hasPageAccess(item.pageKey) ? item : null;
    })
    .filter((item): item is NavigationItem => item !== null);

  const getPriority = (item: NavigationItem) => {
    if (item.pageKey === 'dashboard') return 0;
    if (item.pageKey === 'user-management') return 2;
    return 1;
  };

  const sortedNavigation = accessibleNavigation.slice().sort((a, b) => {
    const priorityA = getPriority(a);
    const priorityB = getPriority(b);
    if (priorityA !== priorityB) return priorityA - priorityB;
    if (priorityA === 1)
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return 0;
  });

  useEffect(() => {
    const activeParent = sortedNavigation.find((item) =>
      item.children?.some((child) => {
        if (child.href) return location.pathname === child.href;
        return child.children?.some((sub) => location.pathname === sub.href);
      })
    );
    if (activeParent) {
      setOpenMenus((prev) => new Set(prev).add(activeParent.name));
      const activeSubGroup = activeParent.children?.find(
        (child) =>
          child.children?.some((sub) => location.pathname === sub.href)
      );
      if (activeSubGroup) {
        setOpenSubMenus((prev) => new Set(prev).add(activeSubGroup.name));
      }
    }
    // Open-state follows route + filtered nav; listing sortedNavigation would rerun every render (new array ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync collapsibles to pathname only
  }, [location.pathname]);

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSubMenu = (name: string) => {
    setOpenSubMenus((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const isActive = (item: NavigationItem | NavigationChild | NavigationSubChild) =>
    location.pathname === item.href;

  const isParentActive = (item: NavigationItem) =>
    item.children?.some((child) => {
      if (child.href) return location.pathname === child.href;
      return child.children?.some((sub) => location.pathname === sub.href);
    }) ?? false;

  const isSubGroupActive = (child: NavigationChild) =>
    child.children?.some((sub) => location.pathname === sub.href) ?? false;

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="data-[slot=sidebar-menu-button]:!p-1.5"
              render={<Link to="/dashboard" />}
            >
              <span className="text-base font-semibold">Strata</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            {sortedNavigation.map((item) => {
              if (item.children && item.children.length > 0) {
                return (
                  <Collapsible
                    key={item.name}
                    open={openMenus.has(item.name)}
                    onOpenChange={() => toggleMenu(item.name)}
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger
                        render={
                          <SidebarMenuButton
                            isActive={isParentActive(item)}
                          />
                        }
                        onClick={() => {
                          if (
                            !openMenus.has(item.name) &&
                            item.children?.[0]
                          ) {
                            const href = item.children[0].href;
                            if (href) navigate(href);
                          }
                        }}
                      >
                        {item.icon && <item.icon size={18} stroke={1.5} />}
                        <span>{item.name}</span>
                        <IconChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.children.map((child) => {
                            if (child.children && child.children.length > 0) {
                              return (
                                <Collapsible
                                  key={child.name}
                                  open={openSubMenus.has(child.name)}
                                  onOpenChange={() => toggleSubMenu(child.name)}
                                >
                                  <SidebarMenuSubItem className="mt-1">
                                    <CollapsibleTrigger
                                      render={
                                        <SidebarMenuSubButton
                                          isActive={isSubGroupActive(child)}
                                          className="h-auto min-h-7 py-1 whitespace-normal"
                                        />
                                      }
                                      onClick={() => {
                                        if (
                                          !openSubMenus.has(child.name) &&
                                          child.children?.[0]
                                        ) {
                                          const href = child.children[0].href;
                                          if (href) navigate(href);
                                        }
                                      }}
                                    >
                                      <span>{child.name}</span>
                                      <IconChevronRight className="ml-auto shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <SidebarMenuSub className="py-1 gap-0.5">
                                        {child.children.map((sub) => (
                                          <SidebarMenuSubItem key={sub.name}>
                                            <SidebarMenuSubButton
                                              isActive={isActive(sub)}
                                              render={<Link to={sub.href} />}
                                            >
                                              <span>{sub.name}</span>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>
                                        ))}
                                      </SidebarMenuSub>
                                    </CollapsibleContent>
                                  </SidebarMenuSubItem>
                                </Collapsible>
                              );
                            }

                            return (
                              <SidebarMenuSubItem key={child.name}>
                                <SidebarMenuSubButton
                                  isActive={isActive(child)}
                                  render={<Link to={child.href!} />}
                                >
                                  <span>{child.name}</span>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              }

              return (
                <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton
                    isActive={isActive(item)}
                    render={<Link to={item.href!} />}
                  >
                    {item.icon && <item.icon size={18} stroke={1.5} />}
                    <span>{item.name}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <Profile />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
